/* ============================================================================
 * useAutoSave.ts - Auto-save logic for local storage persistence
 * ============================================================================ */

import { useEffect, useState } from 'react';
import type { ProjectData } from '@/shared/types/projectData';
import { storage } from '@/shared/utils/storage';

interface UseAutoSaveParams {
  projectData: ProjectData | null;
  expandedNodeIds: Set<string>;
  isCompactView: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}

/**
 * Hook for auto-saving project data to local storage
 */
export function useAutoSave({
  projectData,
  expandedNodeIds,
  isCompactView,
  setHasUnsavedChanges
}: UseAutoSaveParams) {
  const [localCacheSaveFailed, setLocalCacheSaveFailed] = useState(false);
  // Auto-expand groups on initial data load (if no saved state)
  useEffect(() => {
    const appState = storage.load();
    if (!appState?.expandedNodeIds?.length && projectData?.nodes) {
      // Note: Group IDs would be calculated here but expansion is handled in the parent
      // const groupIds = projectData.nodes
      //   .filter(node => node.type === 'group')
      //   .map(node => node.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData?.projectId]);

  // Auto-save to unified storage on any data change
  useEffect(() => {
    if (projectData) {
      const appState = storage.load() || {
        projectData,
        expandedNodeIds: [],
        settings: {},
        hasUserPreferences: false
      };
      
      // Check if expandedNodeIds or isCompactView has changed from the initial state
      // If it has, mark that user now has preferences
      const hasExpandCollapseChanged = appState.expandedNodeIds && 
        Array.from(expandedNodeIds).sort().join(',') !== appState.expandedNodeIds.sort().join(',');
      
      const hasCompactViewChanged = appState.settings?.isCompactView !== undefined && 
        appState.settings.isCompactView !== isCompactView;
      
      const hasUserInteracted = appState.hasUserPreferences || 
        hasExpandCollapseChanged || 
        hasCompactViewChanged;
      
      const savedLocally = storage.save({
        ...appState,
        projectData,
        expandedNodeIds: Array.from(expandedNodeIds),
        hasUserPreferences: hasUserInteracted, // Mark true once user has made any expand/collapse action
        settings: {
          ...appState.settings,
          isCompactView
        }
      });
      
      // This effect also mirrors server reads and view preferences. Failure
      // cannot create user work; retain any dirty flag set by an actual edit.
      // Only a complete local write clears the local-only flag. The state owner
      // keeps unconfirmed operation tickets independent of this setter.
      if (savedLocally) setHasUnsavedChanges(false);
      setLocalCacheSaveFailed(!savedLocally);
    } else {
      setLocalCacheSaveFailed(false);
    }
  }, [projectData, expandedNodeIds, isCompactView, setHasUnsavedChanges]);
  return { localCacheSaveFailed };
}
